import {
    ChangeEvent,
    FC,
    FormEvent,
    useState,
} from 'react'
import Select from 'react-select'
import { useToggle } from 'react-use'
import {
    Button,
    Col,
    CustomInput,
    FormGroup,
    Label,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from 'reactstrap'

import FormDatePicker from '../../../../components/FormDatePicker/FormDatePicker'
import { RadioButtonsList } from '../../../../components/RadioButtonsList/RadioButtonsList'
import Icon from '../../../../components/style/Icon/Icon'
import { formatDate } from '../../../../helpers/date'
import { translate } from '../../../../helpers/translations/translator'
import { SpecialDayFormData, SpecialDayType } from '../../../entities/SpecialDay/SpecialDay'
import { addDays, eachDayOfInterval, isBefore } from '../../../helpers/date'
import { DateFormat } from '../../../types/dateFormatTypes'

export interface AddSpecialDayProps {
    onSubmit: (data: SpecialDayFormData) => void;
    onCancel: () => void;
    specialDayTypes: SpecialDayType[];
}

const AddSpecialDayForm: FC<AddSpecialDayProps> = ({
    onSubmit,
    onCancel,
    specialDayTypes,
}) => {
    const [specialDayType, setSpecialDayType] = useState(specialDayTypes[0].id)
    const [date, setDate] = useState(new Date())
    const [startDate, setStartDate] = useState(new Date())
    const [endDate, setEndDate] = useState(addDays(new Date(), 1))
    const [description, setDescription] = useState('')
    const [isVisibleInEmployeeApp, setIsVisibleInEmployeeApp] = useState(false)
    const [isMultipleDays, toggleIsMultipleDays] = useToggle(false)

    const dateError = isMultipleDays && isBefore(endDate, startDate)
    const canSubmit = !! description.length && specialDayType && ! dateError

    const validateDate = (): boolean => {
        if (isBefore(endDate, startDate)) {
            return false
        }

        return true
    }

    const submitSpecialDay = (event: FormEvent) => {
        event.preventDefault()

        const days = isMultipleDays
            ? eachDayOfInterval({
                start: startDate,
                end: endDate,
            })
            : [date]

        if (isMultipleDays && ! validateDate()) {
            return
        }

        days.forEach(day => {
            onSubmit({
                date: formatDate(day, DateFormat.apiDate),
                type: specialDayType || specialDayTypes[0].name,
                isVisibleInEmployeeApp,
                description,
            })
        })
    }

    const options = specialDayTypes.map((val) => (
        {
            label: <Icon kind={val.name} />,
            value: val.name,
            id: val.name,
        }
    ))

    return (
        <form onSubmit={(event) => submitSpecialDay(event)}>
            <ModalHeader>{`${translate('pages.specialDays.specialDay')} ${translate('common.add')}`}</ModalHeader>
            <ModalBody>
                <Row>
                    <Col>
                        <RadioButtonsList
                            className="set-start-end-date-time-form__multiple-days-radio-list"
                            checked={Number(isMultipleDays)}
                            buttons={[
                                { label: translate('pages.absences.singleDay') },
                                { label: translate('pages.absences.multipleDays') },
                            ]}
                            onRadioChange={toggleIsMultipleDays}
                        />
                    </Col>
                </Row>
                {! isMultipleDays ? (
                    <div>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <FormGroup>
                                        <Label>{translate('common.date')}</Label>
                                        <FormDatePicker
                                            selected={date}
                                            onChange={setDate}
                                        />
                                    </FormGroup>
                                </FormGroup>
                            </Col>
                            <Col />
                        </Row>
                    </div>
                ) : (
                    <div>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <FormGroup>
                                        <Label>{translate('common.startDate')}</Label>
                                        <FormDatePicker
                                            selected={startDate}
                                            onChange={setStartDate}
                                        />
                                    </FormGroup>
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <FormGroup>
                                        <Label>{translate('common.endDate')}</Label>
                                        <FormDatePicker
                                            selected={endDate}
                                            onChange={setEndDate}
                                            invalid={dateError}
                                            feedback={dateError && translate('pages.management.endDateNeedsToBeLaterThanStartDate')}
                                        />
                                    </FormGroup>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                )}
                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{translate('common.category')}</Label>
                            <Select
                                classNamePrefix="react-select"
                                value={options.find(option => option.id === specialDayType)}
                                placeholder={translate('pages.specialDays.selectIcon')}
                                id="type"
                                options={options}
                                onChange={(value) => setSpecialDayType(value?.id || specialDayTypes[0].id)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{translate('common.description')}</Label>
                            <CustomInput
                                className="form-control"
                                type="text"
                                id="description"
                                name="description"
                                value={description}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <FormGroup check>
                        <Label check>
                            <CustomInput
                                checked={isVisibleInEmployeeApp}
                                label={translate('pages.specialDays.isVisibleInEmployeeApp')}
                                type="checkbox"
                                id="isVisibleInEmployeeApp"
                                onChange={() => setIsVisibleInEmployeeApp(! isVisibleInEmployeeApp)}
                            />
                        </Label>
                    </FormGroup>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button type="button" color="link" id="modal-close" onClick={onCancel}>{translate('common.cancel')}</Button>
                <Button type="submit" color="orange" disabled={! canSubmit}>{translate('common.add')}</Button>
            </ModalFooter>
        </form>
    )
}

export default AddSpecialDayForm
